/* Danachi 2026 - CYBEX.
   A planet-sized data cube adrift in a violet nebula. Fixed behind the page;
   scrolling flies the camera in toward it. */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js';

function canvasTexture(size, draw) {
  var c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  draw(c.getContext('2d'), size);
  var t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/* THREE.Color stores linear-sRGB internally, so reading .r/.g/.b straight out
   and printing them as CSS would darken every merchant-picked colour.
   getHexString() does the sRGB conversion for us. */
function rgbOf(hex) {
  var h = new THREE.Color(hex).getHexString();
  return parseInt(h.slice(0, 2), 16) + ',' + parseInt(h.slice(2, 4), 16) + ',' + parseInt(h.slice(4, 6), 16);
}

/* the cube face: dense circuitry with a starfield of lit windows */
function makeFaceTexture(size, hexA, hexB, hexC) {
  return canvasTexture(size, function (g, s) {
    g.fillStyle = '#06030e';
    g.fillRect(0, 0, s, s);

    var cell = s / 72;

    g.strokeStyle = 'rgba(120,86,255,0.16)';
    g.lineWidth = 1;
    g.beginPath();
    for (var i = 0; i <= 72; i++) {
      g.moveTo(i * cell, 0); g.lineTo(i * cell, s);
      g.moveTo(0, i * cell); g.lineTo(s, i * cell);
    }
    g.stroke();

    g.strokeStyle = 'rgba(150,110,255,0.34)';
    g.lineWidth = Math.max(1, cell * 0.18);
    for (var t = 0; t < 260; t++) {
      var x = Math.round(Math.random() * 72) * cell;
      var y = Math.round(Math.random() * 72) * cell;
      g.beginPath();
      g.moveTo(x, y);
      var steps = 2 + ((Math.random() * 4) | 0);
      for (var k = 0; k < steps; k++) {
        if (Math.random() < 0.5) x += (Math.random() < 0.5 ? -1 : 1) * cell * (1 + ((Math.random() * 5) | 0));
        else y += (Math.random() < 0.5 ? -1 : 1) * cell * (1 + ((Math.random() * 5) | 0));
        g.lineTo(x, y);
      }
      g.stroke();
    }

    for (var r = 0; r < 150; r++) {
      var rx = Math.random() * s;
      var ry = Math.random() * s;
      var len = cell * (4 + Math.random() * 26);
      var grd = g.createLinearGradient(rx, ry, rx, ry + len);
      grd.addColorStop(0, 'rgba(210,180,255,0)');
      grd.addColorStop(0.5, 'rgba(210,180,255,0.5)');
      grd.addColorStop(1, 'rgba(210,180,255,0)');
      g.fillStyle = grd;
      g.fillRect(rx, ry, Math.max(1, cell * 0.5), len);
    }

    /* lit windows - the starfield look on each face. Scaled off area, capped so
       the main-thread cost stays bounded on phones. */
    var lights = Math.min(9000, Math.floor(s * s * 0.007));
    for (var n = 0; n < lights; n++) {
      var px = Math.random() * s;
      var py = Math.random() * s;
      var a = 0.25 + Math.random() * 0.75;
      var roll = Math.random();
      if (roll < 0.55) g.fillStyle = 'rgba(' + hexA + ',' + a + ')';
      else if (roll < 0.8) g.fillStyle = 'rgba(255,255,255,' + a + ')';
      else if (roll < 0.93) g.fillStyle = 'rgba(' + hexB + ',' + a + ')';
      else g.fillStyle = 'rgba(' + hexC + ',' + a + ')';
      var w = 1 + Math.random() * (cell * 0.7);
      g.fillRect(px, py, w, w * (Math.random() < 0.3 ? 2.4 : 1));
    }

    for (var b = 0; b < 40; b++) {
      var bx = Math.random() * s;
      var by = Math.random() * s;
      var rad = cell * (1 + Math.random() * 3);
      var bg = g.createRadialGradient(bx, by, 0, bx, by, rad);
      bg.addColorStop(0, 'rgba(255,255,255,0.95)');
      bg.addColorStop(0.4, 'rgba(' + hexA + ',0.5)');
      bg.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = bg;
      g.beginPath();
      g.arc(bx, by, rad, 0, Math.PI * 2);
      g.fill();
    }
  });
}

function makeGlowTexture(size, rgb) {
  return canvasTexture(size, function (g, s) {
    var grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grd.addColorStop(0, 'rgba(' + rgb + ',0.55)');
    grd.addColorStop(0.35, 'rgba(' + rgb + ',0.18)');
    grd.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, s, s);
  });
}

function mountCybex(root) {
  var canvas = root.querySelector('[data-cybex-canvas]');
  if (!canvas) return null;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mobile = window.matchMedia('(max-width: 749px)').matches;

  var primary = root.dataset.primary || '#9d6bff';
  var secondary = root.dataset.secondary || '#ff00ea';
  var tertiary = root.dataset.tertiary || '#35f7ff';
  var speed = parseFloat(root.dataset.speed || '1');
  if (!isFinite(speed)) speed = 1;
  var cubeSize = parseFloat(root.dataset.cube || '44');
  if (!isFinite(cubeSize) || cubeSize <= 0) cubeSize = 44;
  var showFleet = root.dataset.fleet !== 'false';
  var starCount = parseInt(root.dataset.stars || '2600', 10);
  if (!isFinite(starCount) || starCount < 1) starCount = 2600;
  if (mobile) starCount = Math.round(starCount * 0.45);

  var cPrimary = new THREE.Color(primary);
  var cSecondary = new THREE.Color(secondary);
  var cTertiary = new THREE.Color(tertiary);

  var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: !mobile, alpha: false, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.25 : 1.75));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  var scene = new THREE.Scene();
  var deep = new THREE.Color('#0a0418');
  scene.background = deep;
  /* gentle: the star shell sits at r=500..1600 and must survive the fog */
  scene.fog = new THREE.FogExp2(deep, 0.0004);

  var camera = new THREE.PerspectiveCamera(55, 1, 0.5, 4000);
  camera.position.set(0, 12, 190);

  var junk = { geo: [], mat: [], tex: [] };
  function keepG(g) { junk.geo.push(g); return g; }
  function keepM(m) { junk.mat.push(m); return m; }
  function keepT(t) { junk.tex.push(t); return t; }

  var world = new THREE.Group();
  scene.add(world);

  /* ---------------- CYBEX, the cube ---------------- */
  var faceTex = keepT(makeFaceTexture(mobile ? 512 : 1024, rgbOf(primary), rgbOf(secondary), rgbOf(tertiary)));

  var cube = new THREE.Mesh(
    keepG(new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize)),
    keepM(new THREE.MeshStandardMaterial({
      map: faceTex,
      emissive: 0xffffff,
      emissiveMap: faceTex,
      emissiveIntensity: 1.45,
      roughness: 0.5,
      metalness: 0.65
    }))
  );
  world.add(cube);

  var halo = new THREE.Mesh(
    keepG(new THREE.BoxGeometry(cubeSize * 1.045, cubeSize * 1.045, cubeSize * 1.045)),
    keepM(new THREE.MeshBasicMaterial({
      color: cPrimary,
      transparent: true,
      opacity: 0.16,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false
    }))
  );
  world.add(halo);

  var edges = new THREE.LineSegments(
    keepG(new THREE.EdgesGeometry(new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize))),
    keepM(new THREE.LineBasicMaterial({ color: 0xe4d4ff, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }))
  );
  world.add(edges);

  var bloom = new THREE.Sprite(keepM(new THREE.SpriteMaterial({
    map: keepT(makeGlowTexture(256, rgbOf(primary))),
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    opacity: 0.9,
    fog: false
  })));
  bloom.scale.setScalar(cubeSize * 4.2);
  world.add(bloom);

  /* ---------------- orbital light rings ---------------- */
  var rings = new THREE.Group();
  var ringDefs = [
    { r: cubeSize * 1.5, tilt: 1.32, spin: 0.055, col: cPrimary, op: 0.55 },
    { r: cubeSize * 1.95, tilt: 1.18, spin: -0.036, col: cTertiary, op: 0.34 },
    { r: cubeSize * 2.45, tilt: 1.44, spin: 0.024, col: cSecondary, op: 0.26 }
  ];
  var ringMeshes = [];
  for (var ri = 0; ri < ringDefs.length; ri++) {
    var d = ringDefs[ri];
    var ring = new THREE.Mesh(
      keepG(new THREE.TorusGeometry(d.r, 0.28, 8, 220)),
      keepM(new THREE.MeshBasicMaterial({ color: d.col, transparent: true, opacity: d.op, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }))
    );
    ring.rotation.x = d.tilt;
    ring.rotation.y = ri * 0.5;
    ring.userData.spin = d.spin;
    rings.add(ring);
    ringMeshes.push(ring);
  }
  world.add(rings);

  /* ---------------- starfield ---------------- */
  var sp = new Float32Array(starCount * 3);
  var sc = new Float32Array(starCount * 3);
  var tmp = new THREE.Color();
  var white = new THREE.Color('#ffffff');
  for (var i = 0; i < starCount; i++) {
    var rr = 500 + Math.random() * 1100;
    var th = Math.random() * Math.PI * 2;
    var ph = Math.acos(2 * Math.random() - 1);
    sp[i * 3] = rr * Math.sin(ph) * Math.cos(th);
    sp[i * 3 + 1] = rr * Math.cos(ph);
    sp[i * 3 + 2] = rr * Math.sin(ph) * Math.sin(th);
    var pick = Math.random();
    if (pick < 0.6) tmp.copy(white);
    else if (pick < 0.85) tmp.copy(cPrimary);
    else tmp.copy(cSecondary);
    sc[i * 3] = tmp.r; sc[i * 3 + 1] = tmp.g; sc[i * 3 + 2] = tmp.b;
  }
  var starGeo = keepG(new THREE.BufferGeometry());
  starGeo.setAttribute('position', new THREE.BufferAttribute(sp, 3));
  starGeo.setAttribute('color', new THREE.BufferAttribute(sc, 3));
  var stars = new THREE.Points(starGeo, keepM(new THREE.PointsMaterial({
    size: 7,
    sizeAttenuation: true,
    vertexColors: true,
    transparent: true,
    opacity: 0.95,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: false
  })));
  scene.add(stars);

  /* ---------------- nebula ---------------- */
  var nebulaCount = mobile ? 9 : 18;
  var nebTexA = keepT(makeGlowTexture(256, rgbOf(primary)));
  var nebTexB = keepT(makeGlowTexture(256, rgbOf(secondary)));
  var nebula = new THREE.Group();
  for (var n = 0; n < nebulaCount; n++) {
    var spr = new THREE.Sprite(keepM(new THREE.SpriteMaterial({
      map: n % 3 === 0 ? nebTexB : nebTexA,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.16 + Math.random() * 0.22,
      fog: false
    })));
    var nr = 260 + Math.random() * 620;
    var na = Math.random() * Math.PI * 2;
    spr.position.set(Math.cos(na) * nr, (Math.random() - 0.5) * 460, Math.sin(na) * nr - 200);
    spr.scale.setScalar(320 + Math.random() * 520);
    spr.userData.drift = 0.02 + Math.random() * 0.05;
    nebula.add(spr);
  }
  scene.add(nebula);

  /* ---------------- spiral galaxy ---------------- */
  var gCount = mobile ? 900 : 2200;
  var gp = new Float32Array(gCount * 3);
  var gc = new Float32Array(gCount * 3);
  for (var q = 0; q < gCount; q++) {
    var arm = q % 2;
    var tt = Math.pow(Math.random(), 0.6) * 5.2;
    var ang = tt * 1.5 + arm * Math.PI;
    var rad2 = tt * 16;
    var jitter = (1 - tt / 5.2) * 10 + 3;
    gp[q * 3] = Math.cos(ang) * rad2 + (Math.random() - 0.5) * jitter;
    gp[q * 3 + 1] = (Math.random() - 0.5) * jitter * 0.4;
    gp[q * 3 + 2] = Math.sin(ang) * rad2 + (Math.random() - 0.5) * jitter;
    tmp.copy(cPrimary).lerp(white, 1 - tt / 5.2);
    gc[q * 3] = tmp.r; gc[q * 3 + 1] = tmp.g; gc[q * 3 + 2] = tmp.b;
  }
  var galGeo = keepG(new THREE.BufferGeometry());
  galGeo.setAttribute('position', new THREE.BufferAttribute(gp, 3));
  galGeo.setAttribute('color', new THREE.BufferAttribute(gc, 3));
  var galaxy = new THREE.Points(galGeo, keepM(new THREE.PointsMaterial({
    size: 4.4,
    sizeAttenuation: true,
    vertexColors: true,
    transparent: true,
    opacity: 0.85,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: false
  })));
  galaxy.position.set(430, 250, -520);
  galaxy.rotation.set(-0.7, 0.4, 0.3);
  galaxy.scale.setScalar(2.4);
  scene.add(galaxy);

  /* ---------------- moons ---------------- */
  var moons = new THREE.Group();
  var moonDefs = [
    { r: 26, p: [-330, 120, -240] },
    { r: 10, p: [-210, 78, -140] },
    { r: 15, p: [340, -60, -180] }
  ];
  for (var mi = 0; mi < moonDefs.length; mi++) {
    var md = moonDefs[mi];
    var moon = new THREE.Mesh(
      keepG(new THREE.SphereGeometry(md.r, 32, 24)),
      keepM(new THREE.MeshStandardMaterial({ color: 0x2b2440, roughness: 0.92, metalness: 0.05 }))
    );
    moon.position.set(md.p[0], md.p[1], md.p[2]);
    moons.add(moon);
  }
  scene.add(moons);

  /* ---------------- fleet ---------------- */
  var fleet = new THREE.Group();
  var fighters = [];
  if (showFleet) {
    var hullMat = keepM(new THREE.MeshStandardMaterial({ color: 0x140f26, roughness: 0.45, metalness: 0.8 }));
    var trimMat = keepM(new THREE.MeshBasicMaterial({ color: cPrimary, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));

    var station = new THREE.Group();
    station.add(new THREE.Mesh(keepG(new THREE.BoxGeometry(9, 5, 108)), hullMat));
    for (var f = 0; f < 7; f++) {
      var z = -46 + f * 15;
      var fin = new THREE.Mesh(keepG(new THREE.BoxGeometry(30 - f * 1.6, 2.2, 7)), hullMat);
      fin.position.z = z;
      station.add(fin);
      var strip = new THREE.Mesh(keepG(new THREE.BoxGeometry(30 - f * 1.6, 0.5, 1.1)), trimMat);
      strip.position.set(0, 1.7, z);
      station.add(strip);
      if (f % 2 === 0) {
        var tower = new THREE.Mesh(keepG(new THREE.BoxGeometry(2.4, 12 + f, 2.4)), hullMat);
        tower.position.set(0, 8 + f * 0.5, z);
        station.add(tower);
      }
    }
    var beam = new THREE.Mesh(keepG(new THREE.BoxGeometry(1.6, 1.6, 150)), trimMat);
    beam.position.z = 128;
    station.add(beam);
    station.position.set(-165, -58, -30);
    station.rotation.set(0.05, 0.42, 0.06);
    fleet.add(station);

    for (var sIdx = 0; sIdx < 4; sIdx++) {
      var ship = new THREE.Group();
      ship.add(new THREE.Mesh(keepG(new THREE.BoxGeometry(2.6, 1.1, 7)), hullMat));
      var wing = new THREE.Mesh(keepG(new THREE.BoxGeometry(9, 0.6, 2.4)), hullMat);
      wing.position.z = 1.2;
      ship.add(wing);
      var trail = new THREE.Mesh(keepG(new THREE.BoxGeometry(0.9, 0.9, 34)), trimMat);
      trail.position.z = 20;
      ship.add(trail);
      ship.position.set(-90 + sIdx * 62, -34 + sIdx * 21, 30 - sIdx * 26);
      ship.rotation.y = -0.3 + sIdx * 0.22;
      ship.userData.phase = sIdx * 1.7;
      fleet.add(ship);
      fighters.push(ship);
    }
    scene.add(fleet);
  }

  /* ---------------- light ---------------- */
  scene.add(new THREE.AmbientLight(0x2a1e55, 2.2));
  var coreLight = new THREE.PointLight(cPrimary, 90000, 1400, 2);
  scene.add(coreLight);
  var keyLight = new THREE.DirectionalLight(0xc9b4ff, 1.1);
  keyLight.position.set(-1, 0.8, 1);
  scene.add(keyLight);
  var rimLight = new THREE.DirectionalLight(cSecondary, 0.55);
  rimLight.position.set(1, -0.4, -0.7);
  scene.add(rimLight);

  /* ---------------- resize ---------------- */
  function resize() {
    var w = window.innerWidth;
    var h = window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  window.addEventListener('resize', resize);
  resize();

  /* ---------------- scroll + pointer ---------------- */
  var targetT = 0;
  var scrollT = 0;
  function readScroll() {
    var max = document.documentElement.scrollHeight - window.innerHeight;
    targetT = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
  }
  window.addEventListener('scroll', readScroll, { passive: true });
  readScroll();

  var pointer = { x: 0, y: 0 };
  function onPointer(e) {
    pointer.x = (e.clientX / window.innerWidth - 0.5) * 2;
    pointer.y = (e.clientY / window.innerHeight - 0.5) * 2;
  }
  window.addEventListener('pointermove', onPointer, { passive: true });

  var visible = true;
  function onVis() { visible = !document.hidden; }
  document.addEventListener('visibilitychange', onVis);

  var clock = new THREE.Clock();
  var raf = 0;
  var camX = 0;
  var camY = 0;
  var disposed = false;

  function frame() {
    if (disposed) return;
    raf = requestAnimationFrame(frame);
    if (!visible) return;

    var t = clock.getElapsedTime();
    scrollT += (targetT - scrollT) * 0.055;

    var dist = 190 - scrollT * 118 * speed;
    var swing = scrollT * 0.9;
    camX += ((pointer.x * 26 + Math.sin(swing) * 34) - camX) * 0.035;
    camY += ((-pointer.y * 16 + 12 - scrollT * 14) - camY) * 0.035;
    camera.position.set(camX, camY, dist);
    camera.lookAt(0, 0, 0);

    if (!reduced) {
      cube.rotation.y = t * 0.028;
      cube.rotation.x = Math.sin(t * 0.12) * 0.05;
      halo.rotation.copy(cube.rotation);
      edges.rotation.copy(cube.rotation);

      cube.material.emissiveIntensity = 1.35 + Math.sin(t * 1.4) * 0.22 + Math.sin(t * 3.7) * 0.06;
      halo.material.opacity = 0.14 + Math.sin(t * 1.4) * 0.05;

      for (var ri2 = 0; ri2 < ringMeshes.length; ri2++) {
        ringMeshes[ri2].rotation.z += ringMeshes[ri2].userData.spin * 0.02;
      }

      for (var nb = 0; nb < nebula.children.length; nb++) {
        nebula.children[nb].position.x += Math.sin(t * 0.1 + nb) * nebula.children[nb].userData.drift;
      }

      galaxy.rotation.y += 0.0006;
      stars.rotation.y = t * 0.0016;

      for (var fi = 0; fi < fighters.length; fi++) {
        var sh = fighters[fi];
        sh.position.z += 0.32;
        sh.position.y += Math.sin(t * 0.8 + sh.userData.phase) * 0.06;
        if (sh.position.z > 190) sh.position.z = -320;
      }
    }

    bloom.material.opacity = 0.75 + Math.sin(t * 1.4) * 0.14;

    renderer.render(scene, camera);
  }
  frame();

  root.classList.add('is-ready');
  document.body.classList.add('d-cybex-on');

  return function dispose() {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
    window.removeEventListener('scroll', readScroll);
    window.removeEventListener('pointermove', onPointer);
    document.removeEventListener('visibilitychange', onVis);
    document.body.classList.remove('d-cybex-on');
    root.classList.remove('is-ready');
    for (var a = 0; a < junk.geo.length; a++) junk.geo[a].dispose();
    for (var b2 = 0; b2 < junk.mat.length; b2++) junk.mat[b2].dispose();
    for (var c2 = 0; c2 < junk.tex.length; c2++) junk.tex[c2].dispose();
    renderer.dispose();
    if (renderer.forceContextLoss) renderer.forceContextLoss();
  };
}

function mount(el) {
  if (el.__cybexDispose) return;
  try {
    el.__cybexDispose = mountCybex(el) || null;
  } catch (err) {
    el.classList.add('is-fallback');
    console.warn('[cybex]', err);
  }
}

function unmount(el) {
  if (el && typeof el.__cybexDispose === 'function') {
    el.__cybexDispose();
    el.__cybexDispose = null;
  }
}

function boot(scope) {
  (scope || document).querySelectorAll('[data-danachi-cybex]').forEach(mount);
}

if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', function () { boot(); });

/* the theme editor swaps section markup in and out; tear the old context down
   or every edit stacks another live renderer until the browser drops them */
document.addEventListener('shopify:section:unload', function (e) {
  if (!e.target || !e.target.querySelectorAll) return;
  e.target.querySelectorAll('[data-danachi-cybex]').forEach(unmount);
});
document.addEventListener('shopify:section:load', function (e) {
  boot(e.target && e.target.querySelectorAll ? e.target : document);
});

/* e.persisted means the page is entering the back/forward cache and will be
   restored intact. Disposing here would strip body.d-cybex-on and leave a dead
   context on Back, with no remount path since DOMContentLoaded does not fire
   again. WebGL does not block bfcache and the browser suspends rAF for us. */
window.addEventListener('pagehide', function (e) {
  if (e.persisted) return;
  document.querySelectorAll('[data-danachi-cybex]').forEach(unmount);
});
